import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Play, Pause, RotateCcw, SkipForward, Plus, Gavel,
  Vote, Award, Radio, Users,
} from "lucide-react";

export default function LiveSessionPage() {
  const { sessionId } = useParams();
  const [availableSessions, setAvailableSessions] = useState([]);
  const [sid, setSid] = useState(sessionId || "");
  const [data, setData] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  // Dialogs
  const [newSpeaker, setNewSpeaker] = useState({ delegate_name: "", country: "", duration_seconds: 90 });
  const [openSpeaker, setOpenSpeaker] = useState(false);
  const [newMotion, setNewMotion] = useState({ type: "moderated_caucus", topic: "", duration_minutes: 10, speaking_time_seconds: 90 });
  const [openMotion, setOpenMotion] = useState(false);

  const [scoreForm, setScoreForm] = useState({ delegate_id: "", speaking: 5, leadership: 5, research: 5, diplomacy: 5, notes: "" });
  const [openScore, setOpenScore] = useState(false);
  const [registrations, setRegistrations] = useState([]);

  const load = async () => {
    if (!sid) return;
    const { data: d } = await api.get(`/sessions/${sid}`);
    setData(d);
    setSeconds(d.session.remaining_seconds ?? d.session.timer_seconds);
  };

  useEffect(() => {
    if (!sessionId) {
      // fetch all events + sessions
      api.get("/events").then(async (r) => {
        const all = [];
        for (const ev of r.data) {
          const s = await api.get(`/sessions?event_id=${ev.id}`);
          s.data.forEach((x) => all.push({ ...x, eventTitle: ev.title }));
        }
        setAvailableSessions(all);
        if (all[0] && !sid) setSid(all[0].id);
      });
    }
  }, [sessionId, sid]);

  useEffect(() => { if (sid) load(); /* eslint-disable-next-line */ }, [sid]);
  useEffect(() => {
    if (data?.session?.event_id) {
      api.get(`/registrations?event_id=${data.session.event_id}`).then((r) => setRegistrations(r.data));
    }
  }, [data?.session?.event_id]);

  // Timer
  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s <= 1) {
            setRunning(false);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [running]);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const resetTimer = () => {
    setRunning(false);
    setSeconds(data?.session?.timer_seconds || 90);
  };

  const nextSpeaker = async () => {
    const pending = data.speakers.find((s) => s.status === "pending");
    if (!pending) {
      toast.info("No pending speakers");
      return;
    }
    await api.patch(`/speakers/${pending.id}`, { status: "speaking" });
    await api.patch(`/sessions/${sid}`, { current_speaker_id: pending.id, status: "in_progress" });
    setSeconds(pending.duration_seconds || data.session.timer_seconds);
    setRunning(true);
    load();
  };

  const doneSpeaker = async () => {
    const currentId = data.session.current_speaker_id;
    if (currentId) {
      await api.patch(`/speakers/${currentId}`, { status: "yielded" });
    }
    setRunning(false);
    load();
  };

  const addSpeaker = async (e) => {
    e.preventDefault();
    try {
      await api.post("/speakers", {
        session_id: sid,
        delegate_id: "manual",
        delegate_name: newSpeaker.delegate_name,
        country: newSpeaker.country,
        duration_seconds: Number(newSpeaker.duration_seconds),
      });
      setOpenSpeaker(false);
      setNewSpeaker({ delegate_name: "", country: "", duration_seconds: 90 });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const proposeMotion = async (e) => {
    e.preventDefault();
    try {
      await api.post("/motions", {
        session_id: sid,
        proposed_by: "chair",
        ...newMotion,
        duration_minutes: Number(newMotion.duration_minutes),
        speaking_time_seconds: Number(newMotion.speaking_time_seconds),
      });
      setOpenMotion(false);
      setNewMotion({ type: "moderated_caucus", topic: "", duration_minutes: 10, speaking_time_seconds: 90 });
      load();
      toast.success("Motion proposed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const passMotion = async (m, status) => {
    await api.patch(`/motions/${m.id}`, { status });
    load();
  };

  const submitScore = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scores", {
        session_id: sid,
        ...scoreForm,
        speaking: Number(scoreForm.speaking),
        leadership: Number(scoreForm.leadership),
        research: Number(scoreForm.research),
        diplomacy: Number(scoreForm.diplomacy),
      });
      setOpenScore(false);
      toast.success("Score saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!sid) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-[#0A192F]">Pick a session</h1>
          <div className="mt-6 space-y-3">
            {availableSessions.map((s) => (
              <button
                key={s.id}
                className="w-full text-left p-4 rounded-xl bg-white border border-slate-200 hover:border-[#D4AF37]"
                onClick={() => setSid(s.id)}
                data-testid={`pick-session-${s.id}`}
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  {s.eventTitle}
                </p>
                <p className="font-semibold text-[#0A192F] mt-1">{s.agenda}</p>
              </button>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) return <DashboardLayout><p>Loading live session…</p></DashboardLayout>;

  const currentSpeaker = data.speakers.find((s) => s.id === data.session.current_speaker_id);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Live session
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tighter text-[#0A192F]">
              {data.session.agenda}
            </h1>
          </div>
          <Link to="/dashboard/awards">
            <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81]" data-testid="go-awards-button">
              <Award className="h-4 w-4 mr-1" /> Awards room
            </Button>
          </Link>
        </div>

        {/* Timer + current speaker */}
        <div className="mt-6 rounded-2xl bg-[#0A192F] text-white border border-[#D4AF37]/30 p-8">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold flex items-center gap-1">
                <Radio className="h-3 w-3 text-emerald-400 animate-pulse" /> LIVE
              </p>
              <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">Current speaker</p>
              <h2 className="mt-1 text-2xl font-semibold">
                {currentSpeaker ? `${currentSpeaker.country} — ${currentSpeaker.delegate_name}` : "— no one on the floor —"}
              </h2>
            </div>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">Time</p>
              <p className="font-mono text-6xl text-[#D4AF37] mt-2" data-testid="live-timer">
                {formatTime(seconds)}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-end gap-2">
              {running ? (
                <Button onClick={() => setRunning(false)} className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold" data-testid="pause-timer-button">
                  <Pause className="h-4 w-4 mr-1" /> Pause
                </Button>
              ) : (
                <Button onClick={() => setRunning(true)} className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold" data-testid="play-timer-button">
                  <Play className="h-4 w-4 mr-1" /> Play
                </Button>
              )}
              <Button variant="outline" onClick={resetTimer} className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="reset-timer-button">
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button onClick={nextSpeaker} className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="next-speaker-button">
                <SkipForward className="h-4 w-4 mr-1" /> Next
              </Button>
              {currentSpeaker && (
                <Button variant="outline" onClick={doneSpeaker} className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="done-speaker-button">
                  Done
                </Button>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue="speakers" className="mt-8">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="speakers" data-testid="live-tab-speakers">Speakers list</TabsTrigger>
            <TabsTrigger value="motions" data-testid="live-tab-motions">Motions & Votes</TabsTrigger>
            <TabsTrigger value="scores" data-testid="live-tab-scores">Scoring</TabsTrigger>
          </TabsList>

          <TabsContent value="speakers" className="mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Queue</h3>
              <Dialog open={openSpeaker} onOpenChange={setOpenSpeaker}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="add-speaker-button">
                    <Plus className="h-4 w-4 mr-1" /> Add speaker
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add speaker</DialogTitle></DialogHeader>
                  <form onSubmit={addSpeaker} className="space-y-4" data-testid="speaker-form">
                    <div>
                      <Label>Country / Portfolio</Label>
                      <Input required value={newSpeaker.country} onChange={(e) => setNewSpeaker({ ...newSpeaker, country: e.target.value })} />
                    </div>
                    <div>
                      <Label>Delegate name</Label>
                      <Input required value={newSpeaker.delegate_name} onChange={(e) => setNewSpeaker({ ...newSpeaker, delegate_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Duration (s)</Label>
                      <Input type="number" value={newSpeaker.duration_seconds} onChange={(e) => setNewSpeaker({ ...newSpeaker, duration_seconds: e.target.value })} />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Add</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="mt-4 rounded-xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  <tr>
                    <th className="px-5 py-3 text-left w-16">#</th>
                    <th className="px-5 py-3 text-left">Country</th>
                    <th className="px-5 py-3 text-left">Delegate</th>
                    <th className="px-5 py-3 text-left">Time</th>
                    <th className="px-5 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.speakers.map((s) => (
                    <tr key={s.id} className={`border-t border-slate-100 ${s.id === data.session.current_speaker_id ? "bg-[#D4AF37]/10" : ""}`}>
                      <td className="px-5 py-3 font-mono text-xs">{s.order}</td>
                      <td className="px-5 py-3 font-semibold text-[#0A192F]">{s.country}</td>
                      <td className="px-5 py-3 text-slate-600">{s.delegate_name}</td>
                      <td className="px-5 py-3 font-mono text-xs">{s.duration_seconds}s</td>
                      <td className="px-5 py-3">
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold">
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {data.speakers.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-slate-500">Queue empty. Add a speaker to begin.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="motions" className="mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Motions</h3>
              <Dialog open={openMotion} onOpenChange={setOpenMotion}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="propose-motion-button">
                    <Gavel className="h-4 w-4 mr-1" /> Propose motion
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New motion</DialogTitle></DialogHeader>
                  <form onSubmit={proposeMotion} className="space-y-4" data-testid="motion-form">
                    <div>
                      <Label>Type</Label>
                      <Select value={newMotion.type} onValueChange={(v) => setNewMotion({ ...newMotion, type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="moderated_caucus">Moderated Caucus</SelectItem>
                          <SelectItem value="unmoderated_caucus">Unmoderated Caucus</SelectItem>
                          <SelectItem value="introduce_working_paper">Introduce Working Paper</SelectItem>
                          <SelectItem value="vote">Vote</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Topic</Label>
                      <Input required value={newMotion.topic} onChange={(e) => setNewMotion({ ...newMotion, topic: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Duration (min)</Label>
                        <Input type="number" value={newMotion.duration_minutes} onChange={(e) => setNewMotion({ ...newMotion, duration_minutes: e.target.value })} />
                      </div>
                      <div>
                        <Label>Speaking time (s)</Label>
                        <Input type="number" value={newMotion.speaking_time_seconds} onChange={(e) => setNewMotion({ ...newMotion, speaking_time_seconds: e.target.value })} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Propose</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {data.motions.map((m) => (
                <div key={m.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`motion-card-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
                      {m.type.replace(/_/g, " ")}
                    </p>
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${m.status === "passed" ? "bg-emerald-100 text-emerald-700" : m.status === "failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                      {m.status}
                    </span>
                  </div>
                  <h4 className="mt-2 font-semibold text-[#0A192F]">{m.topic}</h4>
                  <p className="text-xs text-slate-500 mt-1">{m.duration_minutes}min · {m.speaking_time_seconds}s each</p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="p-2 rounded bg-emerald-50 text-emerald-700">For {m.votes_for}</div>
                    <div className="p-2 rounded bg-red-50 text-red-700">Against {m.votes_against}</div>
                    <div className="p-2 rounded bg-slate-100 text-slate-700">Abstain {m.abstain}</div>
                  </div>
                  {m.status === "pending" && (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => passMotion(m, "passed")} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid={`pass-motion-${m.id}`}>
                        Pass
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => passMotion(m, "failed")} className="flex-1" data-testid={`fail-motion-${m.id}`}>
                        Fail
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {data.motions.length === 0 && <p className="text-sm text-slate-500">No motions yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="scores" className="mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">Delegate scoring</h3>
              <Dialog open={openScore} onOpenChange={setOpenScore}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="add-score-button">
                    <Vote className="h-4 w-4 mr-1" /> Add score
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Record a score</DialogTitle></DialogHeader>
                  <form onSubmit={submitScore} className="space-y-3" data-testid="score-form">
                    <div>
                      <Label>Delegate</Label>
                      <Select value={scoreForm.delegate_id} onValueChange={(v) => setScoreForm({ ...scoreForm, delegate_id: v })}>
                        <SelectTrigger data-testid="score-delegate-select"><SelectValue placeholder="Pick delegate" /></SelectTrigger>
                        <SelectContent>
                          {registrations.map((r) => (
                            <SelectItem key={r.delegate_id} value={r.delegate_id}>
                              {r.delegate_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {["speaking", "leadership", "research", "diplomacy"].map((k) => (
                        <div key={k}>
                          <Label className="capitalize">{k}</Label>
                          <Input type="number" min={0} max={10} value={scoreForm[k]} onChange={(e) => setScoreForm({ ...scoreForm, [k]: e.target.value })} />
                        </div>
                      ))}
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea value={scoreForm.notes} onChange={(e) => setScoreForm({ ...scoreForm, notes: e.target.value })} rows={3} />
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]">Save score</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="mt-4 rounded-xl bg-white border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  <tr>
                    <th className="px-5 py-3 text-left">Delegate</th>
                    <th className="px-5 py-3 text-center">Spk</th>
                    <th className="px-5 py-3 text-center">Lead</th>
                    <th className="px-5 py-3 text-center">Rsr</th>
                    <th className="px-5 py-3 text-center">Dip</th>
                    <th className="px-5 py-3 text-center">Total</th>
                    <th className="px-5 py-3 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.scores.map((s) => (
                    <tr key={s.id} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-semibold text-[#0A192F]">
                        {registrations.find((r) => r.delegate_id === s.delegate_id)?.delegate_name || s.delegate_id}
                      </td>
                      <td className="px-5 py-3 text-center font-mono text-xs">{s.speaking}</td>
                      <td className="px-5 py-3 text-center font-mono text-xs">{s.leadership}</td>
                      <td className="px-5 py-3 text-center font-mono text-xs">{s.research}</td>
                      <td className="px-5 py-3 text-center font-mono text-xs">{s.diplomacy}</td>
                      <td className="px-5 py-3 text-center font-mono text-xs text-[#D4AF37] font-bold">
                        {s.speaking + s.leadership + s.research + s.diplomacy}
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">{s.notes}</td>
                    </tr>
                  ))}
                  {data.scores.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-10 text-slate-500">No scores yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {data.scores.length > 0 && (
              <div className="mt-4 rounded-xl bg-[#0A192F] text-white p-5 flex items-center gap-3">
                <Users className="h-5 w-5 text-[#D4AF37]" />
                <p className="text-sm">
                  {data.scores.length} scores recorded — head to{" "}
                  <Link className="underline" to="/dashboard/awards">awards room</Link>{" "}
                  to get AI award recommendations.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
